import { useRef, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Eraser, PenTool, Type } from 'lucide-react';
import { Input } from './ui/input';

const SignatureCanvas = ({ onSignatureChange, width = 400, height = 150, label = "Signature", initialTypedName = "" }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureMode, setSignatureMode] = useState('draw'); // 'draw' or 'type'
  const [typedName, setTypedName] = useState(initialTypedName);

  useEffect(() => {
    if (signatureMode === 'draw') {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      
      // Set up canvas
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.strokeStyle = '#0A2342';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [width, height, signatureMode]);

  // Generate typed signature as image
  const generateTypedSignatureImage = (name) => {
    if (!name.trim()) {
      onSignatureChange(null, null, 'type');
      return;
    }
    
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    
    // Draw typed signature in a handwriting-like font style
    ctx.fillStyle = '#0A2342';
    ctx.font = 'italic 32px "Georgia", serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name, width / 2, height / 2);
    
    const signatureData = canvas.toDataURL('image/png');
    onSignatureChange(signatureData, name, 'type');
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      // Export signature as base64 image
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL('image/png');
      onSignatureChange(signatureData, null, 'draw');
    }
  };

  const clearSignature = () => {
    if (signatureMode === 'draw') {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      setHasSignature(false);
    } else {
      setTypedName('');
    }
    onSignatureChange(null, null, signatureMode);
  };

  const handleModeSwitch = (mode) => {
    setSignatureMode(mode);
    setHasSignature(false);
    setTypedName('');
    onSignatureChange(null, null, mode);
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">{label} *</label>
      
      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant={signatureMode === 'draw' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleModeSwitch('draw')}
          className={signatureMode === 'draw' ? 'bg-[#0A2342]' : ''}
        >
          <PenTool className="w-4 h-4 mr-1" /> Draw Signature
        </Button>
        <Button
          type="button"
          variant={signatureMode === 'type' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleModeSwitch('type')}
          className={signatureMode === 'type' ? 'bg-[#0A2342]' : ''}
        >
          <Type className="w-4 h-4 mr-1" /> Type Name
        </Button>
      </div>

      {signatureMode === 'draw' ? (
        <div className="relative">
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            className="border-2 border-gray-300 rounded-lg cursor-crosshair bg-white touch-none w-full"
            style={{ maxWidth: `${width}px`, aspectRatio: `${width}/${height}` }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            data-testid="signature-canvas"
          />
          {hasSignature && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={clearSignature}
              className="absolute top-2 right-2 bg-white"
            >
              <Eraser className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
          <p className="text-xs text-gray-500 mt-1">Sign above using your mouse or finger</p>
        </div>
      ) : (
        <div className="space-y-2">
          <Input
            value={typedName}
            onChange={(e) => {
              setTypedName(e.target.value);
              generateTypedSignatureImage(e.target.value);
            }}
            placeholder="Type your full legal name"
            className="text-lg"
            data-testid="signature-typed-input"
          />
          {typedName && (
            <div className="border-2 border-gray-300 rounded-lg p-4 bg-white">
              <p className="text-center text-2xl italic text-[#0A2342]" style={{ fontFamily: 'Georgia, serif' }}>
                {typedName}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearSignature}
                className="mt-2 mx-auto block"
              >
                <Eraser className="w-4 h-4 mr-1" /> Clear
              </Button>
            </div>
          )}
          <p className="text-xs text-gray-500">Your typed name will be rendered as a signature</p>
        </div>
      )}
    </div>
  );
};

export default SignatureCanvas;
